import type { MutationTree } from 'vuex';

import {
  WinningMandateStep,
  type WinningMandateFormData,
  type WinningMandateLocation,
  type WinningMandateProfileChipVisibility,
  type WinningMandateSelectedListing,
} from '@/models/winningMandate';
import type { AgencyAgent } from '@/types-agency';

export interface WinningMandateState extends WinningMandateFormData {
  currentStep: WinningMandateStep;
  profileChipVisibility: WinningMandateProfileChipVisibility;
}

export const getDefaultProfileChipVisibility = (): WinningMandateProfileChipVisibility => ({
  sales: true,
  listings: true,
  teamSize: true,
});

export const getInitialWinningMandateState = (): WinningMandateState => ({
  currentStep: WinningMandateStep.GetStarted,
  sellerFirstName: '',
  sellerLastName: '',
  propertyAddress: null,
  includeLocationReport: false,
  includeHedonicValuation: false,
  hedonicValuationFile: null,
  selectedAgent: null,
  selectedListings: [],
  salesCount: 0,
  listingsCount: 0,
  teamSize: 0,
  profileChipVisibility: getDefaultProfileChipVisibility(),
});

export const getters = {
  getWinningMandateCurrentStep: (state: WinningMandateState): WinningMandateStep =>
    state.currentStep,
  getWinningMandateSellerFirstName: (state: WinningMandateState): string => state.sellerFirstName,
  getWinningMandateSellerLastName: (state: WinningMandateState): string => state.sellerLastName,
  getWinningMandatePropertyAddress: (state: WinningMandateState): WinningMandateLocation | null =>
    state.propertyAddress,
  getWinningMandateIncludeLocationReport: (state: WinningMandateState): boolean =>
    state.includeLocationReport,
  getWinningMandateIncludeHedonicValuation: (state: WinningMandateState): boolean =>
    state.includeHedonicValuation,
  getWinningMandateHedonicValuationFile: (state: WinningMandateState): File | null =>
    state.hedonicValuationFile,
  getWinningMandateSelectedAgent: (state: WinningMandateState): AgencyAgent | null =>
    state.selectedAgent,
  getWinningMandateSelectedListings: (
    state: WinningMandateState,
  ): WinningMandateSelectedListing[] => state.selectedListings,
  getWinningMandateSalesCount: (state: WinningMandateState): number => state.salesCount,
  getWinningMandateListingsCount: (state: WinningMandateState): number => state.listingsCount,
  getWinningMandateTeamSize: (state: WinningMandateState): number => state.teamSize,
  getWinningMandateProfileChipVisibility: (
    state: WinningMandateState,
  ): WinningMandateProfileChipVisibility => state.profileChipVisibility,
};

export const mutations: MutationTree<WinningMandateState> = {
  setWinningMandateCurrentStep(state, step: WinningMandateStep): void {
    state.currentStep = step;
  },
  setWinningMandateSellerFirstName(state, sellerFirstName: string): void {
    state.sellerFirstName = sellerFirstName;
  },
  setWinningMandateSellerLastName(state, sellerLastName: string): void {
    state.sellerLastName = sellerLastName;
  },
  setWinningMandatePropertyAddress(state, propertyAddress: WinningMandateLocation | null): void {
    state.propertyAddress = propertyAddress;
  },
  setWinningMandateIncludeLocationReport(state, includeLocationReport: boolean): void {
    state.includeLocationReport = includeLocationReport;
  },
  setWinningMandateIncludeHedonicValuation(state, includeHedonicValuation: boolean): void {
    state.includeHedonicValuation = includeHedonicValuation;
    if (!includeHedonicValuation) {
      state.hedonicValuationFile = null;
    }
  },
  setWinningMandateHedonicValuationFile(state, hedonicValuationFile: File | null): void {
    state.hedonicValuationFile = hedonicValuationFile;
  },
  setWinningMandateSelectedAgent(state, selectedAgent: AgencyAgent | null): void {
    state.selectedAgent = selectedAgent;
  },
  setWinningMandateSelectedListings(
    state,
    selectedListings: WinningMandateSelectedListing[],
  ): void {
    state.selectedListings = selectedListings;
  },
  setWinningMandateSalesCount(state, salesCount: number): void {
    state.salesCount = salesCount;
  },
  setWinningMandateListingsCount(state, listingsCount: number): void {
    state.listingsCount = listingsCount;
  },
  setWinningMandateTeamSize(state, teamSize: number): void {
    state.teamSize = teamSize;
  },
  setWinningMandateProfileChipVisibility(
    state,
    profileChipVisibility: WinningMandateProfileChipVisibility,
  ): void {
    state.profileChipVisibility = profileChipVisibility;
  },
  resetWinningMandateState(state): void {
    Object.assign(state, getInitialWinningMandateState());
  },
};

const winningMandateStore = {
  state: getInitialWinningMandateState,
  getters,
  mutations,
};

export default winningMandateStore;
